import React from "react";
import { Handle, NodeProps, Position, useConnection } from "@xyflow/react";
import classNames from "classnames";
import { ConsolidationTreatment } from "@common/types/domains/accounting/consolidation";
import { useUser } from "@hooks/useUser";
import { OwnershipEntityIdentity } from "../OwnershipEntityIdentity";
import { OwnershipEntityNode } from "../ownership.types";
import {
    OWNERSHIP_DROP_ZONE_HANDLE_ID,
    getOwnershipSourceHandleStyle,
    getOwnershipSourceHandles,
} from "./ownershipConnection.helpers";
import styles from "./EntityNode.module.scss";

export const EntityNode: React.FC<NodeProps<OwnershipEntityNode>> = React.memo(
    ({ id, data, selected }) => {
        const { entity, subtitle, treatment } = data;
        const currentUser = useUser();
        const isCurrentUserPersonalEntity =
            entity.isPersonal &&
            entity.user != null &&
            entity.userId === currentUser?.id;

        const isConnecting = useConnection((state) => state.inProgress);
        const isHoveredValidTarget = useConnection(
            (state) =>
                state.inProgress &&
                state.isValid === true &&
                state.toNode?.id === id,
        );

        return (
            <div
                className={classNames(styles.card, {
                    [styles.cardSelected]: selected,
                    [styles.cardConnecting]: isConnecting,
                    [styles.cardDropTarget]: isHoveredValidTarget,
                })}
            >
                <Handle
                    type="target"
                    position={Position.Top}
                    className={styles.edgeAnchor}
                    isConnectable={false}
                />
                {getOwnershipSourceHandles().map((handle) => (
                    <Handle
                        key={handle.position}
                        id={handle.id}
                        type="source"
                        position={handle.position}
                        className={styles.sourceHandle}
                        style={getOwnershipSourceHandleStyle()}
                    />
                ))}
                <OwnershipEntityIdentity entity={entity}>
                    {treatment ===
                        ConsolidationTreatment.doesNotConsolidate && (
                        <span className={styles.treatmentBadge}>
                            Not consolidated
                        </span>
                    )}
                </OwnershipEntityIdentity>
                <div className={styles.subtitle}>
                    {isCurrentUserPersonalEntity ? "Owner (you)" : subtitle}
                </div>
                <Handle
                    id={OWNERSHIP_DROP_ZONE_HANDLE_ID}
                    type="target"
                    position={Position.Top}
                    className={styles.dropZone}
                    isConnectableStart={false}
                />
            </div>
        );
    },
);
EntityNode.displayName = "EntityNode";
